import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import { useEffectOnce } from 'hooks';
import { useProductListGTMEvents } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type {
	ColorField,
	Field,
	SitecoreBlockProps,
	SitecoreLink,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { getAsset, is } from 'utils/helpers';

import PromoStartPage from './PromoStartPage';

interface Category {
	fields: {
		icon?: Field<DigizuiteAsset[]>;
		title: Field<string>;
	};
	id: string;
	url: string;
}

interface SupportItem {
	displayName: string;
	fields: {
		campaignBubbleLabel?: Field<string>;
		campaignBubbleSale?: Field<boolean>;
		campaignBubbleText?: Field<string>;
		landscapeImage?: Field<DigizuiteAsset[]>;
		link?: Field<SitecoreLink>;
	};
	id: string;
}

interface Props extends SitecoreBlockProps {
	fields: {
		campaignBubbleLabel?: Field<string>;
		campaignBubbleSale?: Field<boolean>;
		campaignBubbleText?: Field<string>;
		categoryLinks?: Category[];
		datasourceName?: string;
		heading?: Field<string>;
		largeImage?: Field<DigizuiteAsset[]>;
		preventAutoplay?: Field<boolean>;
		primaryLink?: Field<SitecoreLink>;
		products?: ProductCard[];
		promoSupportItems?: SupportItem[];
		secondaryLink?: Field<SitecoreLink>;
		smallImage?: Field<DigizuiteAsset[]>;
		tertiaryLink?: Field<SitecoreLink>;
		textBackgroundColor?: ColorField;
		video?: Field<DigizuiteAsset[]>;
	};
}

/**
 * Media grid and category links.
 *
 * - One large primary image/video area with product animation and links.
 * - Two smaller secondary image/video areas with a link.
 * - List of level one categories.
 *
 * Order of secondary items and category links is different for desktop/mobile.
 */
export default function PromoStartPageBlock({ fields, params }: Props) {
	const {
		categoryLinks,
		datasourceName,
		primaryLink,
		products,
		secondaryLink,
		tertiaryLink,
	} = fields;
	const primaryLargeVideo = getAsset('Source Copy', fields.video);
	const primarySmallVideo = getAsset('Source Copy', fields.video);
	const primaryLargeImage = getAsset('Templated 2:1', fields.largeImage);
	const primarySmallImage = getAsset('Templated 4:3', fields.smallImage);

	const primaryLinks = [primaryLink, secondaryLink, tertiaryLink]
		.map((link) => getValidSitecoreLink(link?.value))
		.filter(is.truthy);

	const supportItems = (fields.promoSupportItems ?? [])
		.map((item) => {
			const image = getAsset('Templated 2:1', item.fields.landscapeImage);
			return {
				campaignBubbleLabel: item.fields.campaignBubbleLabel?.value,
				campaignBubbleSale: Boolean(item.fields.campaignBubbleSale?.value),
				campaignBubbleText: item.fields.campaignBubbleText?.value,
				id: item.id,
				imageSrc: image?.src,
				imageAlt: image?.alt ?? '',
				link: getValidSitecoreLink(item.fields.link?.value),
				// Currently no video support for support items.
				video: undefined,
			};
		})
		.filter((item) => item.imageSrc || item.video)
		.slice(0, 2);

	const categoryIcons = filterTruthy(
		(categoryLinks ?? []).map((cat) => ({
			iconUrl: getAsset('Source Copy', cat.fields.icon)?.src,
			title: cat.fields.title.value,
			url: cat.url,
		})),
		'title',
		'url',
	);

	const hasPrimaryImage =
		is.truthy(primarySmallImage) && is.truthy(primaryLargeImage);
	const hasPrimaryVideo =
		is.truthy(primarySmallVideo) && is.truthy(primaryLargeVideo);
	const hasRequiredData =
		supportItems.length === 2 && (hasPrimaryImage || hasPrimaryVideo);

	const { gtmItemListId, gtmItemListName, sendViewItemListEventOnce } =
		useProductListGTMEvents(
			'block',
			`Block: ${datasourceName ?? 'PromoStartPageUnknown'}`,
		);
	useEffectOnce(() => {
		if (products?.length) {
			sendViewItemListEventOnce(products, products.length);
		}
	});

	if (!hasRequiredData) {
		return (
			<ComponentPlaceholder
				componentName="PromoStartPage"
				description="Requires a primary image/video and two support items"
				className={getEditorMargin(params)}
			/>
		);
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
		>
			<PromoStartPage
				basePriceTextBackground={
					fields.textBackgroundColor?.fields?.colorHex?.value
				}
				campaignBubbleLabel={fields.campaignBubbleLabel?.value}
				campaignBubbleSale={fields.campaignBubbleSale?.value}
				campaignBubbleText={fields.campaignBubbleText?.value}
				categoryIcons={categoryIcons}
				gtmItemListId={gtmItemListId}
				gtmItemListName={gtmItemListName}
				heading={fields.heading}
				headingLevel={params?.heading}
				preventAutoplay={fields.preventAutoplay?.value}
				primaryLargeImageAlt={primaryLargeImage?.alt}
				primaryLargeImageSrc={primaryLargeImage?.src}
				primaryLargeVideo={primaryLargeVideo}
				primaryLinkHref={
					primaryLink?.value.linktype === 'popover'
						? undefined
						: primaryLink?.value.href
				}
				primaryLinks={primaryLinks}
				primarySmallImageAlt={primarySmallImage?.alt}
				primarySmallImageSrc={primarySmallImage?.src}
				primarySmallVideo={primarySmallVideo}
				products={products}
				supportItems={supportItems}
			/>
		</LayoutContainer>
	);
}
PromoStartPageBlock.displayName = 'PromoStartPageBlock';
